import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { apiFetch } from '../utils/apiFetch';
import LocationConfirmation, { locationAddressKey, validMapPosition } from './LocationConfirmation';
import OriginSetupNotice from './OriginSetupNotice';
import useOriginSetup from '../hooks/useOriginSetup';
import { getCatalogStorageSession, isCatalogStorageSessionCurrent } from '../utils/catalogSessionStorage';

const toFormTiers = (tiers = []) => (tiers.length ? tiers : [{ maxDistanceKm: 1 }]).map((tier, index) => ({
  maxDistanceKm: index + 1,
  deliveryFee: tier.deliveryFeeCents === null || tier.deliveryFeeCents === undefined
    ? ''
    : (Number(tier.deliveryFeeCents) / 100).toFixed(2),
  preparationMinutes: tier.preparationMinutes ?? '',
}));

const addressLabel = (address = {}) => [
  [address.street, address.number].filter(Boolean).join(', '),
  address.neighborhood,
  [address.city, address.state].filter(Boolean).join(' - '),
  address.cep,
].filter(Boolean).join(', ');

const syncLabel = (state = {}) => {
  if (state.status === 'synced') return { label: 'Sincronizado', color: 'success' };
  if (state.error) return { label: 'Erro de sincronização', color: 'error' };
  return { label: 'Pendente', color: 'warning' };
};
const originConsumerLabel = (consumerState, outboxState = {}, setupStatus = '') => {
  if (!consumerState && outboxState.status === 'synced' && setupStatus && setupStatus !== 'ready') {
    return { label: 'Aplicação aguardando confirmação', color: 'warning' };
  }
  if (consumerState?.required === false) return { label: 'Não requerido', color: 'default' };
  if (consumerState?.applied === true) return { label: 'Sincronizado', color: 'success' };
  if (consumerState?.failed === true) return { label: 'Erro de sincronização', color: 'error' };
  if (consumerState) return { label: 'Pendente', color: 'warning' };
  return syncLabel(outboxState);
};

const DeliveryPricingPanel = ({ apiUrl, onManagementChange }) => {
  const originSetup = useOriginSetup(apiUrl);
  const [loading, setLoading] = useState(true);
  const locationRef = useRef(null);
  const locationSectionRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const ownerSessionRef = useRef(getCatalogStorageSession());
  const [mapPreview, setMapPreview] = useState(null);
  const role = String(getCatalogStorageSession()?.user?.role || '').toLowerCase();
  const canManageAddress = ['programador', 'administrador'].includes(role);
  const [saving, setSaving] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [messageSeverity, setMessageSeverity] = useState('success');
  const [config, setConfig] = useState(null);
  const [storeAddressSync, setStoreAddressSync] = useState(null);
  const [companyAddress, setCompanyAddress] = useState({});
  const companyAddressRef = useRef(companyAddress);
  companyAddressRef.current = companyAddress;
  const [persistedCompanyAddress, setPersistedCompanyAddress] = useState({});
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [addressSearchSource, setAddressSearchSource] = useState('');
  const [tiers, setTiers] = useState([{ maxDistanceKm: 1, deliveryFee: '', preparationMinutes: '' }]);

  const load = useCallback(async ({ preserveDrafts = false } = {}) => {
    if (!preserveDrafts) setLoading(true);
    setError('');
    try {
      const response = await apiFetch(`${apiUrl}/api/delivery-pricing/config`);
      const data = await response.json().catch(() => ({}));
      if (!isCatalogStorageSessionCurrent(ownerSessionRef.current)) return;
      if (!response.ok) throw new Error(data.error || 'Falha ao carregar entrega e preparo.');
      setConfig(data.config || null);
      setStoreAddressSync(data.storeAddressSync || null);
      if (!preserveDrafts) {
        setCompanyAddress(data.company?.address || {});
        setPersistedCompanyAddress(data.company?.address || {});
        setTiers(toFormTiers(data.config?.tiers));
      }
      onManagementChange?.(data.integrationManagement || {});
      if (data.imported) setMessage('Configuração existente do Brutos Cardápio importada com segurança.');
    } catch (loadError) {
      setError(loadError.message || 'Falha ao carregar entrega e preparo.');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, onManagementChange]);

  useEffect(() => {
    load();
    return () => window.clearTimeout(refreshTimerRef.current);
  }, [load]);

  useEffect(() => {
    const key = locationAddressKey(companyAddress);
    const requiredFields = ['street', 'number', 'neighborhood', 'city', 'state'];
    setMapPreview(null);
    if (!canManageAddress || requiredFields.some((field) => !String(companyAddress[field] || '').trim())) return undefined;

    let active = true;
    const session = ownerSessionRef.current;
    const timer = window.setTimeout(async () => {
      try {
        const response = await apiFetch(`${apiUrl}/api/delivery-pricing/store-address-preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ address: {
            street: companyAddress.street || '', number: companyAddress.number || '',
            neighborhood: companyAddress.neighborhood || '', city: companyAddress.city || '', state: companyAddress.state || '', country: 'BR',
          } }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Não foi possível localizar a região deste endereço.');
        if (!active || !isCatalogStorageSessionCurrent(session)
          || locationAddressKey(companyAddressRef.current) !== key) return;
        const center = validMapPosition(data.mapPreview?.center);
        const suggestedPosition = validMapPosition(data.mapPreview?.suggestedPosition);
        setMapPreview({ key, center, suggestedPosition, quality: String(data.mapPreview?.quality || ''), unavailable: !center });
      } catch {
        if (active && isCatalogStorageSessionCurrent(session)
          && locationAddressKey(companyAddressRef.current) === key) {
          setMapPreview({ key, center: null, quality: '', unavailable: true });
        }
      }
    }, 450);

    return () => { active = false; window.clearTimeout(timer); };
  }, [apiUrl, canManageAddress, companyAddress]);

  useEffect(() => {
    const cep = String(companyAddress.cep || '').replace(/\D/g, '').slice(0, 8);
    const street = String(companyAddress.street || '').trim();
    if (!addressSearchSource || (addressSearchSource === 'cep' ? cep.length !== 8 : street.length < 3)) {
      if (addressSearchSource) setAddressSuggestions([]);
      return undefined;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      setSuggestionsLoading(true);
      try {
        const response = await apiFetch(`${apiUrl}/api/delivery-pricing/address-suggestions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            q: addressSearchSource === 'cep' ? cep : street,
            ...(addressSearchSource === 'cep' && cep ? { postalCode: cep } : {}),
            ...(companyAddress.city ? { city: companyAddress.city } : {}),
            ...(companyAddress.state ? { state: companyAddress.state } : {}),
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Falha ao consultar endereços.');
        if (!active || !isCatalogStorageSessionCurrent(ownerSessionRef.current)) return;
        const suggestions = Array.isArray(data.suggestions) ? data.suggestions : [];
        setAddressSuggestions(suggestions);
        if (addressSearchSource === 'cep' && suggestions.length === 1) {
          const resolved = suggestions[0].address || {};
          setCompanyAddress((current) => ({
            ...current,
            ...resolved,
            cep: resolved.cep || cep,
            number: resolved.number || current.number || '',
          }));
          setAddressSearchSource('');
        } else if (addressSearchSource === 'cep' && suggestions.length > 1) {
          setMessageSeverity('info');
          setMessage('Encontramos mais de um endereço para este CEP. Selecione a opção correta no campo Rua.');
        }
      } catch (suggestError) {
        if (active) setError(suggestError.message || 'Falha ao consultar endereços no Photon.');
      } finally {
        if (active) setSuggestionsLoading(false);
      }
    }, 350);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [addressSearchSource, apiUrl, companyAddress.cep, companyAddress.city, companyAddress.state, companyAddress.street]);

  const addressComplete = useMemo(() => {
    const address = companyAddress || {};
    return String(address.cep || '').replace(/\D/g, '').length === 8
      && ['street', 'number', 'neighborhood', 'city', 'state'].every((field) => String(address[field] || '').trim());
  }, [companyAddress]);

  const canSave = useMemo(() => Boolean(addressLabel(persistedCompanyAddress)) && tiers.every((tier) => (
    tier.deliveryFee !== '' && Number.isFinite(Number(String(tier.deliveryFee).replace(',', '.')))
    && Number(String(tier.deliveryFee).replace(',', '.')) >= 0
    && Number.isInteger(Number(tier.preparationMinutes))
    && Number(tier.preparationMinutes) > 0
  )), [persistedCompanyAddress, tiers]);

  const updateAddress = (field, value) => {
    setCompanyAddress((current) => ({ ...current, [field]: value }));
  };

  const selectSuggestion = (suggestion) => {
    if (!suggestion?.address) return;
    setCompanyAddress((current) => ({
      ...current,
      ...suggestion.address,
      cep: current.cep || suggestion.address.cep || '',
      number: suggestion.address.number || current.number || '',
    }));
    setAddressSearchSource('');
    setAddressSuggestions([]);
  };

  const updateTier = (index, field, value) => {
    setTiers((current) => current.map((tier, tierIndex) => (
      tierIndex === index ? { ...tier, [field]: value } : tier
    )));
  };

  const addTier = () => {
    setTiers((current) => [
      ...current,
      { maxDistanceKm: current.length + 1, deliveryFee: '', preparationMinutes: '' },
    ]);
  };

  const removeLastTier = () => {
    setTiers((current) => current.length > 1 ? current.slice(0, -1) : current);
  };

  const saveAddress = async () => {
    if (!canManageAddress) return;
    const ownerSession = ownerSessionRef.current;
    const submittedAddress = companyAddress;
    const submittedKey = locationAddressKey(submittedAddress);
    if (!isCatalogStorageSessionCurrent(ownerSession)) return;
    setSavingAddress(true);
    setError('');
    setMessage('');
    try {
      const response = await apiFetch(`${apiUrl}/api/delivery-pricing/store-address`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: companyAddress, locationConfirmation: locationRef.current?.getValue()?.confirmationToken }),
      });
      const data = await response.json().catch(() => ({}));
      if (!isCatalogStorageSessionCurrent(ownerSession)) return;
      if (!response.ok) {
        if (String(data.code || '').includes('LOCATION') || String(data.code || '').includes('CONFIRMATION')) {
          locationRef.current?.openMap(data.position || data.details?.position);
        }
        throw new Error(data.error || 'Falha ao salvar o endereço da loja. Confira e confirme a entrada no mapa.');
      }
      if (locationAddressKey(companyAddressRef.current) === submittedKey) setCompanyAddress(data.company?.address || submittedAddress);
      setPersistedCompanyAddress(data.company?.address || submittedAddress);
      setStoreAddressSync(data.storeAddressSync || null);
      if (data.config) setConfig(data.config);
      setMessageSeverity('success');
      setMessage('Endereço salvo. A replicação para Entregas, Cardápio e Zap foi iniciada com segurança.');
      originSetup.refresh();
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = window.setTimeout(() => load({ preserveDrafts: true }), 800);
    } catch (saveError) {
      setError(saveError.message || 'Falha ao salvar o endereço da loja.');
    } finally {
      setSavingAddress(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await apiFetch(`${apiUrl}/api/delivery-pricing/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tiers: tiers.map((tier) => ({
            maxDistanceKm: tier.maxDistanceKm,
            deliveryFeeCents: Math.round(Number(String(tier.deliveryFee).replace(',', '.')) * 100),
            preparationMinutes: Number(tier.preparationMinutes),
          })),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao salvar as faixas.');
      setConfig(data.config || config);
      setMessageSeverity('success');
      setMessage('Faixas salvas. Cardápio e Zap serão atualizados automaticamente.');
      originSetup.refresh();
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = window.setTimeout(() => load({ preserveDrafts: true }), 800);
    } catch (saveError) {
      setError(saveError.message || 'Falha ao salvar as faixas.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 220 }}><CircularProgress /></Box>;
  }

  const currentAddressKey = locationAddressKey(companyAddress);
  const currentMapPreview = mapPreview?.key === currentAddressKey ? mapPreview : null;
  const savedStorePosition = [
    [originSetup.setup?.origin, originSetup.setup?.address],
    [storeAddressSync, storeAddressSync?.address],
    [config?.origin, config?.origin?.address],
  ].find(([position, address]) => position && address && locationAddressKey(address) === currentAddressKey)?.[0] || null;

  return (
    <Stack spacing={2.5} className="configuracoes-section">
      <OriginSetupNotice state={originSetup} disabled={saving || savingAddress}
        hasDraft={locationAddressKey(companyAddress) !== locationAddressKey(persistedCompanyAddress)}
        onConfirm={() => {
          locationRef.current?.openMap(originSetup.setup?.origin);
          locationSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        }} />
      <Card className="configuracoes-card">
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="subtitle2" color="text.secondary">Entrega e Preparo</Typography>
            <Typography variant="h5" fontWeight={900}>Taxa e prazo por distância de rota</Typography>
            <Typography color="text.secondary">
              A distância é calculada pelo OSRM a partir do endereço da loja. Cada valor vale para entregas de até o limite indicado.
            </Typography>
            <Alert severity={addressLabel(persistedCompanyAddress) ? 'info' : 'warning'}>
              <strong>Centro da rota:</strong> {addressLabel(persistedCompanyAddress) || 'Endereço da loja incompleto. Salve o endereço abaixo antes de configurar as faixas.'}
            </Alert>
            {config?.source === 'cardapio-legacy-import' && (
              <Alert severity="success">Configuração preservada e importada do Brutos Cardápio.</Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}
            {message && <Alert severity={messageSeverity}>{message}</Alert>}
          </Stack>
        </CardContent>
      </Card>

      <Card className="configuracoes-card">
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={900}>Endereço da loja</Typography>
            <Typography color="text.secondary">
              Este endereço será o centro das rotas e será replicado para Brutos Entregas, Cardápio e Zap.
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                label="CEP"
                value={companyAddress.cep || ''}
                onChange={(event) => {
                  const cep = event.target.value.replace(/\D/g, '').slice(0, 8);
                  updateAddress('cep', cep);
                  setAddressSearchSource(cep.length === 8 ? 'cep' : '');
                }}
                inputProps={{ inputMode: 'numeric', maxLength: 8 }}
                helperText="Ao informar 8 dígitos, buscamos o endereço no Photon."
                required
                sx={{ minWidth: { md: 220 } }}
              />
              <Autocomplete
                freeSolo
                fullWidth
                filterOptions={(options) => options}
                options={addressSuggestions}
                loading={suggestionsLoading}
                inputValue={companyAddress.street || ''}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onInputChange={(_, value, reason) => {
                  if (reason !== 'input') return;
                  updateAddress('street', value);
                  setAddressSearchSource(value.trim().length >= 3 ? 'street' : '');
                }}
                onChange={(_, value) => selectSuggestion(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Rua"
                    required
                    helperText="Digite ao menos 3 caracteres e selecione a sugestão com bairro, cidade e CEP corretos."
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Stack>
                      <Typography>{option.address?.street || option.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{option.label}</Typography>
                    </Stack>
                  </li>
                )}
              />
              <TextField
                label="Número"
                value={companyAddress.number || ''}
                onChange={(event) => updateAddress('number', event.target.value)}
                required
                sx={{ minWidth: { md: 180 } }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField label="Bairro" value={companyAddress.neighborhood || ''} onChange={(event) => updateAddress('neighborhood', event.target.value)} fullWidth required />
              <TextField label="Cidade" value={companyAddress.city || ''} onChange={(event) => updateAddress('city', event.target.value)} fullWidth required />
              <TextField label="UF" value={companyAddress.state || ''} onChange={(event) => updateAddress('state', event.target.value.toUpperCase().slice(0, 2))} inputProps={{ maxLength: 2 }} required sx={{ minWidth: { md: 130 } }} />
            </Stack>
            {canManageAddress && <Box ref={locationSectionRef}><LocationConfirmation
              ref={locationRef} apiUrl={apiUrl} purpose="store"
              addressDetails={companyAddress} initialPosition={savedStorePosition}
              initialPositionQuality={savedStorePosition ? 'saved-origin-suggestion' : ''}
              initialSuggestedPosition={savedStorePosition || currentMapPreview?.suggestedPosition}
              initialSuggestedPositionQuality={savedStorePosition ? 'saved-origin-suggestion' : currentMapPreview?.quality || ''}
              initialMapCenter={currentMapPreview?.center}
              initialMapPreviewQuality={currentMapPreview?.quality || (savedStorePosition ? 'house' : '')}
              mapPreviewUnavailable={currentMapPreview?.unavailable && !savedStorePosition}
              storeOriginVerified={originSetup.setup?.originVerified}
              disabled={savingAddress}
            /></Box>}
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {storeAddressSync && ['entregas', 'cardapio', 'zap'].map((consumer) => {
                  const item = originSetup.setup?.syncState?.[consumer] || storeAddressSync.syncState?.[consumer] || {};
                  const state = originConsumerLabel(originSetup.setup?.consumerStates?.[consumer], item, originSetup.setup?.status);
                  const name = consumer === 'entregas' ? 'Brutos Entregas' : consumer === 'cardapio' ? 'Cardápio' : 'Brutos Zap';
                  return <Chip key={consumer} label={`${name}: ${state.label}`} color={state.color} />;
                })}
              </Stack>
              <Button variant="contained" onClick={saveAddress} disabled={!canManageAddress || savingAddress || !addressComplete}>
                {savingAddress ? 'Salvando endereço...' : 'Salvar endereço da loja'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card className="configuracoes-card">
        <CardContent>
          <Stack spacing={2}>
            {tiers.map((tier, index) => (
              <Stack
                key={tier.maxDistanceKm}
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', md: 'center' }}
              >
                <Chip label={`Até ${tier.maxDistanceKm} km`} color="primary" sx={{ minWidth: 110 }} />
                <TextField
                  label="Taxa de entrega (R$)"
                  value={tier.deliveryFee}
                  onChange={(event) => updateTier(index, 'deliveryFee', event.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                  fullWidth
                  required
                />
                <TextField
                  label="Prazo estimado (min)"
                  type="number"
                  value={tier.preparationMinutes}
                  onChange={(event) => updateTier(index, 'preparationMinutes', event.target.value)}
                  inputProps={{ min: 1, max: 1440, step: 1 }}
                  fullWidth
                  required
                />
                {index === tiers.length - 1 && tiers.length > 1 && (
                  <Tooltip title={`Excluir faixa de ${tier.maxDistanceKm} km`}>
                    <IconButton color="error" onClick={removeLastTier} aria-label={`Excluir faixa de ${tier.maxDistanceKm} km`}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            ))}

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
              <Button startIcon={<AddCircleOutlineIcon />} variant="outlined" onClick={addTier} disabled={tiers.length >= 50}>
                Adicionar +1 km
              </Button>
              <Button variant="contained" onClick={save} disabled={saving || !canSave}>
                {saving ? 'Salvando...' : 'Salvar entrega e preparo'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {config?.active && (
        <Card className="configuracoes-card">
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Typography fontWeight={800}>Replicação:</Typography>
              {['cardapio-digital', 'zap'].map((consumer) => {
                const state = syncLabel(config.syncState?.[consumer]);
                return <Chip key={consumer} label={`${consumer === 'zap' ? 'Brutos Zap' : 'Cardápio'}: ${state.label}`} color={state.color} />;
              })}
              <Typography variant="caption" color="text.secondary">Revisão {config.revision}</Typography>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

export default DeliveryPricingPanel;
